/* Product card — the behaviour of the card of the collection grid, wherever it
 * is shown: the grid itself, the search results, the sliders of the home page
 * and the two bands at the foot of the product sheet.
 *
 * Why a file of its own rather than a {% javascript %} block in the snippet:
 * `shopify theme dev` serves the compiled bundles truncated past a threshold
 * that moves from one run to the next, and a bundle that does not parse
 * registers nothing at all. This block was the largest of `snippet-scripts.js`
 * by a third, and on 16 Sept. 2026 the bundle went over again — the cards of
 * the related-products and recently-viewed bands had no behaviour at all, with
 * `customElements.get('product-card')` undefined on the page. Files in
 * `assets/` are served whole. Same rule, and same reason, as the gallery.
 *
 * **Who loads it.** The sections that render cards do, each once, rather than
 * the snippet, which a collection page renders forty-eight times over:
 * `collection`, `search`, `product-slider` and the `product-band` snippet. A
 * row fetched into a band needs nothing of its own — the band already carries
 * it. A new place showing cards has to add the tag.
 *
 * Roles, as for the cart: the Liquid file (`snippets/product-card.liquid`)
 * carries the markup, the classes read here and the CSS; this file carries the
 * behaviour alone.
 *
 * What the component does:
 *   - steps through the media of the card with its `<` `>` arrows (desktop);
 *   - below 900px, where the media lie in a row swiped with scroll-snap (CSS
 *     of the snippet), moves the progress bar along with the scroll
 *     (`--scroll-start` on the card), loads every picture as soon as the row
 *     is swiped, and switches snapping on at `load` (`data-card-snap`):
 *     WebKit recomputes the snap position when the first image arrives while
 *     the page loads and would open the card on another picture (same guard
 *     as the gallery, see product-gallery.js);
 *   - fades each image in once it has loaded;
 *   - fetches the sibling colours on the first hover, one request serving every
 *     card of the same group on the page;
 *   - previews a hovered sibling in place — its image over the card's own, its
 *     sizes and price in the footer — and puts the card back on leaving.
 *
 * Markup contract (what the script looks for in the DOM):
 *
 *   <product-card> containing (with `--scroll-width`, one picture's share of
 *   the progress bar, in its style attribute when it has several):
 *     .product-card__image    the media, all but one carrying `hidden`
 *                             (lifted by the CSS below 900px, where the row
 *                             scrolls instead)
 *     .product-card__arrow    optional, with data-step -1 or 1
 *     .product-card__media    the frame: scroll container below 900px, where
 *                             a preview image is placed
 *     .product-card__colours  optional, with data-color-ref and data-color-query
 *     .product-card__sizes / .product-card__price   swapped during a preview
 *   and, per swatch, a <template class="color-swatch__preview">.
 *
 * Wrapped in a function because a page can carry the tag twice — the product
 * sheet holds two bands. At the top level of a classic script a `class` or a
 * `const` lands in the shared global scope, and the second copy would throw on
 * "already declared" before ever reaching the guard below.
 */

(() => {
  /* Sibling colour swatches. Liquid cannot look products up by the value of a
     metafield, so the group is fetched from the `color-swatches` section (see
     it for why the search is on the first word of the reference only). The
     fetch waits for the first hover, when the swatches become visible, and one
     request serves every card of the same group on the page. */
  const swatchGroups = new Map();

  function loadSwatchGroup(query) {
    if (!swatchGroups.has(query)) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const url = `${root}search?q=${encodeURIComponent(query)}&type=product&section_id=color-swatches`;
      swatchGroups.set(
        query,
        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .catch(() => '')
      );
    }
    return swatchGroups.get(query);
  }

  /* Same order as on the product page (`product-colors.js`): by `data-order`,
     the product id, each sibling slipped in before the first swatch that comes
     after it, so that the card's own swatch never moves — it may hold the
     focus that triggered the fetch. */
  function insertInOrder(list, swatch) {
    const order = Number(swatch.dataset.order);
    const next = [...list.children].find((item) => Number(item.dataset.order) > order);
    list.insertBefore(swatch, next || null);
  }

  /* Image carousel of the card (`<` `>` step through the media) and the
     fade-in of each image once it has loaded. */
  class ProductCard extends HTMLElement {
    connectedCallback() {
      this.images = [...this.querySelectorAll('.product-card__image')];
      this.index = Math.max(0, this.images.findIndex((image) => !image.hidden));
      this.images.forEach((image) => {
        if (image.complete && image.naturalWidth > 0) image.classList.add('is-loaded');
        else image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });
      });
      this.addEventListener('click', this.onClick);

      /* Mobile swipe: the frame only scrolls below 900px (elsewhere the
         pictures are stacked and hidden), so the listener is harmless on
         desktop and no media query is needed. */
      this.media = this.querySelector('.product-card__media');
      if (this.media && this.images.length > 1) {
        this.media.addEventListener('scroll', this.onScroll, { passive: true });
        if (document.readyState === 'complete') this.dataset.cardSnap = '';
        else window.addEventListener('load', () => { this.dataset.cardSnap = ''; }, { once: true });
      }

      this.colours = this.querySelector('.product-card__colours');
      if (this.colours && this.colours.dataset.colorQuery) {
        this.addEventListener('pointerenter', this.onReveal);
        this.addEventListener('focusin', this.onReveal);
        this.colours.addEventListener('pointerover', this.onSwatchEnter);
        this.colours.addEventListener('focusin', this.onSwatchEnter);
        this.colours.addEventListener('pointerleave', this.onSwatchLeave);
        this.colours.addEventListener('focusout', this.onSwatchLeave);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      if (this.media) this.media.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('pointerenter', this.onReveal);
      this.removeEventListener('focusin', this.onReveal);
      if (this.colours) {
        this.colours.removeEventListener('pointerover', this.onSwatchEnter);
        this.colours.removeEventListener('focusin', this.onSwatchEnter);
        this.colours.removeEventListener('pointerleave', this.onSwatchLeave);
        this.colours.removeEventListener('focusout', this.onSwatchLeave);
      }
    }

    /* Swatches are hidden below 900px, so nothing is fetched there. */
    onReveal = () => {
      this.removeEventListener('pointerenter', this.onReveal);
      this.removeEventListener('focusin', this.onReveal);
      if (window.matchMedia('(max-width: 900px)').matches) return;
      this.addSiblingSwatches();
    };

    async addSiblingSwatches() {
      const list = this.colours;
      const { colorRef, colorQuery } = list.dataset;
      const html = await loadSwatchGroup(colorQuery);
      if (!html) return;
      const own = list.querySelector('.color-swatch');
      const handle = own ? own.dataset.handle : '';
      const swatches = new DOMParser().parseFromString(html, 'text/html');
      swatches.querySelectorAll('.color-swatch').forEach((swatch) => {
        if (swatch.dataset.colorRef !== colorRef) return;
        if (swatch.dataset.handle === handle) return;
        /* The swatches come from another document; importing them clones the
           preview template with its contents, which plain adoption leaves
           ambiguous per spec. */
        insertInOrder(list, document.importNode(swatch, true));
      });
    }

    /* Preview the hovered sibling in place: its image over the card's own, its
       sizes and price in the footer. `focusout` fires before the `focusin` of
       the next swatch, hence the restore bound to leaving the list as a whole. */
    onSwatchEnter = (event) => {
      const swatch = event.target.closest('.color-swatch');
      if (!swatch) return;
      const preview = swatch.querySelector('.color-swatch__preview');
      if (!preview) {
        /* The current colour carries no preview: hovering it back means coming
           back to the card's own product, just as leaving the list does. */
        this.onSwatchLeave();
        return;
      }

      if (!this.ownPreview) {
        const sizes = this.querySelector('.product-card__sizes');
        const price = this.querySelector('.product-card__price');
        this.ownPreview = {
          sizes: sizes ? sizes.innerHTML : null,
          price: price ? price.innerHTML : null,
          hadSizes: this.classList.contains('product-card--sizes'),
        };
      }

      this.clearPreviewImage();
      const image = preview.content.querySelector('.product-card__image--preview');
      if (image) {
        const clone = image.cloneNode(true);
        if (clone.complete && clone.naturalWidth > 0) clone.classList.add('is-loaded');
        else clone.addEventListener('load', () => clone.classList.add('is-loaded'), { once: true });
        this.querySelector('.product-card__media').append(clone);
      }

      this.classList.add('is-previewing');

      const sizes = preview.content.querySelector('.product-card__sizes');
      const sizeTarget = this.querySelector('.product-card__sizes');
      if (sizes && sizeTarget) {
        sizeTarget.innerHTML = sizes.innerHTML;
        this.classList.toggle('product-card--sizes', sizes.innerHTML.trim() !== '');
      }

      const price = preview.content.querySelector('.product-card__price');
      const priceTarget = this.querySelector('.product-card__price');
      if (price && priceTarget) priceTarget.innerHTML = price.innerHTML;
    };

    onSwatchLeave = () => {
      if (!this.ownPreview) return;
      this.classList.remove('is-previewing');
      this.clearPreviewImage();
      const sizes = this.querySelector('.product-card__sizes');
      const price = this.querySelector('.product-card__price');
      if (sizes && this.ownPreview.sizes !== null) sizes.innerHTML = this.ownPreview.sizes;
      if (price && this.ownPreview.price !== null) price.innerHTML = this.ownPreview.price;
      this.classList.toggle('product-card--sizes', this.ownPreview.hadSizes);
      this.ownPreview = null;
    };

    clearPreviewImage() {
      const previous = this.querySelector('.product-card__image--preview');
      if (previous) previous.remove();
    }

    /* The black part of the bar is one picture wide (`--scroll-width`, from
       Liquid) and starts at the scrolled share of the row, as on the original
       (`scrollLeft / scrollWidth`). The first swipe also lifts the lazy
       loading of the other pictures: left to the browser, each would only be
       asked for once swiped into view, grey meanwhile. */
    onScroll = () => {
      const { scrollLeft, scrollWidth } = this.media;
      this.style.setProperty('--scroll-start', `${(scrollLeft / scrollWidth) * 100}%`);
      if (!this.swiped) {
        this.swiped = true;
        this.images.forEach((image) => {
          if (image.loading === 'lazy') image.loading = 'eager';
        });
      }
    };

    onClick = (event) => {
      const arrow = event.target.closest('.product-card__arrow');
      if (!arrow || this.images.length < 2) return;
      event.preventDefault();
      const step = Number(arrow.dataset.step) || 1;
      this.show((this.index + step + this.images.length) % this.images.length);
    };

    show(index) {
      this.images[this.index].hidden = true;
      this.index = index;
      const image = this.images[index];
      image.hidden = false;
      if (image.loading === 'lazy') image.loading = 'eager';
    }
  }

  if (!customElements.get('product-card')) {
    customElements.define('product-card', ProductCard);
  }
})();
